import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../../ReactQuery/useQuery';
import { get } from '../../../../redux/api';
import type { VcsGitHubStatusResponse } from '../../../../../shared/types/routes/internalTools/vcsGitHub';

export const getVcsGitHubStatusQueryKey = (): QueryKey => ['internal-tools', 'vcs-github', 'status'];

export const ACTIVE_JOB_POLL_INTERVAL_MS = 5000;

/** Polls only while a migration job is queued or running; idle status costs a single request. */
export const getStatusRefetchInterval = (status: VcsGitHubStatusResponse | undefined): number | false =>
  status?.activeJob ? ACTIVE_JOB_POLL_INTERVAL_MS : false;

function getVcsGitHubStatus() {
  return get<VcsGitHubStatusResponse>('/api/internal-tools/vcs-github/status');
}

/**
 * Fetches the current org's vcs-service migration status for the admin dashboard's VCS GitHub
 * migration card: the overall migration state, the VCS_SERVICE_IMPORT flag, counts for both sides
 * (legacy installations and projects against fossaCore connections and their linked projects), the
 * latest migration jobs, and the one in flight if there is one.
 *
 * DB-only and cheap, unlike the preview, which dry-runs the backfill against vcs-service.
 *
 * Polls while a migration job is queued or running so the in-flight callout clears and
 * Rollback re-enables once it finishes; the shared client disables focus refetches, so
 * nothing else would refresh it.
 *
 * @param {boolean} enabled - Whether to run the query. The card stays mounted
 * while the Backfill accordion is collapsed, so pass `false` until it opens.
 */
export function useGetVcsGitHubStatus(enabled: boolean) {
  return useQuery<VcsGitHubStatusResponse>({
    queryKey: getVcsGitHubStatusQueryKey(),
    queryFn: getVcsGitHubStatus,
    enabled,
    refetchInterval: getStatusRefetchInterval,
  });
}
